import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

/* Change your own password (POST /auth/change-password). People who sign in
   with their HR employee code change their HR app password, so the ERP and
   the HR app keep one password. `forced`: still on the admin's common HR
   password — the dialog can't be closed until they choose their own. */
export default function ChangePassword({ open, onOpenChange, forced = false }) {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ current: "", next: "", again: "" });
  const [busy, setBusy] = useState(false);
  const hr = !!user?.hr_password;
  const min = hr ? 10 : 6;

  const save = async (e) => {
    e.preventDefault();
    if (f.next.length < min || (hr && (!/[A-Za-z]/.test(f.next) || !/\d/.test(f.next)))) {
      return toast.error(hr ? "Use at least 10 characters, with letters and numbers" : "Use at least 6 characters");
    }
    if (f.next !== f.again) return toast.error("The new passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current: f.current, new: f.next });
      toast.success(hr ? "Password changed — use it for the ERP and the HR app" : "Password changed");
      setUser((u) => (u ? { ...u, must_change_password: false } : u));
      setF({ current: "", next: "", again: "" });
      onOpenChange(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        // The ERP sign-in has ended (e.g. signed out in another tab of this browser).
        toast.error("Your sign-in has ended — sign in again, then choose your password.");
        await logout();
        nav("/login", { replace: true });
      } else {
        toast.error(errText(err));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!forced) onOpenChange(v); }}>
      <DialogContent
        className="max-w-md"
        data-testid="change-password"
        onInteractOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
      >
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{forced ? "Choose your own password" : "Change password"}</DialogTitle>
            <DialogDescription>
              {forced
                ? "You signed in with the common password your admin gave out. Choose a password only you know before you continue."
                : hr
                  ? "This is the password you use with your employee code, for both the ERP and the HR app."
                  : "Change the password you sign in to the ERP with."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-4">
            <div>
              <Label htmlFor="cp-current">{forced ? "Common password (the one you just used)" : "Current password"}</Label>
              <PasswordInput id="cp-current" autoComplete="current-password" value={f.current}
                onChange={(e) => setF({ ...f, current: e.target.value })} required data-testid="cp-current" />
            </div>
            <div>
              <Label htmlFor="cp-new">New password</Label>
              <PasswordInput id="cp-new" autoComplete="new-password" value={f.next}
                onChange={(e) => setF({ ...f, next: e.target.value })} required data-testid="cp-new" />
              <p className="text-[11px] text-slate-500 mt-1">{hr ? "At least 10 characters, with letters and numbers." : "At least 6 characters."}</p>
            </div>
            <div>
              <Label htmlFor="cp-again">Repeat the new password</Label>
              <PasswordInput id="cp-again" autoComplete="new-password" value={f.again}
                onChange={(e) => setF({ ...f, again: e.target.value })} required data-testid="cp-again" />
            </div>
          </div>
          <DialogFooter>
            {!forced && <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>}
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="cp-save">Change password</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* Opens the dialog by itself, and keeps it open, while the signed-in user is
   still on the common password. Mounted once in the app layout. */
export function ForcedPasswordChange() {
  const { user } = useAuth();
  const must = !!user?.must_change_password;
  return <ChangePassword open={must} onOpenChange={() => {}} forced />;
}
